import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import showToast from '@/components/common/Toast';
import I18n from '@/modules/i18n';
import {
  plato_api_bulk_create_model_correlations_tags_path,
  plato_api_bulk_destroy_model_correlations_tags_path,
  plato_api_model_correlation_tags_path,
  plato_api_model_correlations_tags_path,
} from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import showUndoToast from '../showUndoToast';
import { CandidateStandard, candidateLabel, ModelCorrelation } from '../types';

// Kept off correlationMutationKey: the table's rows disable themselves by matching the mutating id,
// and a candidate's tag id would collide with an unrelated row's model_tags id.
export const candidateMutationKey = (modelType: string, modelId: number) => (
  ['candidateAdd', modelType, modelId]
);

const postCorrelations = (body: Record<string, unknown>) => (
  axios.post<{ ids: number[] }>(plato_api_bulk_create_model_correlations_tags_path(), body)
);

const postCorrelation = (body: Record<string, unknown>) => (
  axios
    .post<{ data: ModelCorrelation }>(plato_api_model_correlations_tags_path(), body)
    .then(response => response.data.data)
);

type BatchResponse = { data: { ids: number[] } };

interface UseAddCorrelationParams {
  modelId: number;
  modelType: string;
  onBulkFailure: () => void;
  onBulkUndo: () => void;
  onFailure: (candidate: CandidateStandard) => void;
  onUndo: (candidate: CandidateStandard) => void;
  /** Sent with every mutation so the API can refresh that program's counts. */
  programId?: number;
}

export default function useAddCorrelation({
  modelId, modelType, onBulkFailure, onBulkUndo, onFailure, onUndo, programId,
}: UseAddCorrelationParams) {
  const queryClient = useQueryClient();
  // Omitted entirely when there is no program, so the API sees a blank param rather than "undefined".
  const programParams = programId ? { program_id: programId } : {};

  const invalidate = () => {
    queryClient.invalidateQueries([QueryKeys.ModelCorrelations, modelType, modelId]);
    queryClient.invalidateQueries([QueryKeys.CorrelationCandidates, modelType, modelId]);
  };

  const addMutation = useMutation({
    mutationFn: ({ tag_id: tagId }: CandidateStandard) => postCorrelation({
      model_id: modelId, model_type: modelType, tag_id: tagId, ...programParams,
    }),
    mutationKey: candidateMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  // Undoing an add deletes the row it wrote — always a `tci` row, so this is a real delete rather
  // than the soft-hide an EdGate-sourced correlation would get.
  const undoMutation = useMutation({
    mutationFn: (correlation: ModelCorrelation) => axios
      .delete(plato_api_model_correlation_tags_path(correlation.id, programParams)),
    mutationKey: candidateMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  const bulkAddMutation = useMutation({
    mutationFn: (candidates: CandidateStandard[]) => postCorrelations({
      model_id: modelId,
      model_type: modelType,
      tag_ids: candidates.map(({ tag_id: tagId }) => tagId),
      ...programParams,
    }),
    mutationKey: candidateMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  // Undoing a batch is a bulk remove of exactly the rows it wrote, which the table's endpoint already
  // does — and they are all `tci`, so it deletes rather than hides them.
  const bulkUndoMutation = useMutation({
    mutationFn: (ids: number[]) => axios
      .post<{ ids: number[] }>(plato_api_bulk_destroy_model_correlations_tags_path(), { ids, ...programParams }),
    mutationKey: candidateMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  const { mutateAsync: sendAdd } = addMutation;
  const { mutateAsync: sendUndo } = undoMutation;
  const { mutateAsync: sendBulkAdd } = bulkAddMutation;
  const { mutateAsync: sendBulkUndo } = bulkUndoMutation;

  const addCorrelation = useCallback((candidate: CandidateStandard) => {
    const standard = candidateLabel(candidate);
    const failed = () => {
      onFailure(candidate);
      showToast(I18n.t('correlation_action_error'), { msgType: 'error' });
    };

    sendAdd(candidate)
      .then(correlation => showUndoToast({
        announcement: I18n.t('correlation_added_announcement', { standard }),
        message: I18n.t('correlation_added', { standard }),
        onUndo: () => {
          onUndo(candidate);
          sendUndo(correlation).catch(failed);
        },
        toastId: `candidate-${candidate.tag_id}`,
      }))
      .catch(failed);
  }, [onFailure, onUndo, sendAdd, sendUndo]);

  const batchFailed = useCallback(() => {
    onBulkFailure();
    showToast(I18n.t('correlation_action_error'), { msgType: 'error' });
  }, [onBulkFailure]);

  const undoBatch = useCallback((ids: number[]) => {
    onBulkUndo();
    sendBulkUndo(ids).catch(batchFailed);
  }, [batchFailed, onBulkUndo, sendBulkUndo]);

  // Counts what the server actually wrote, not what was selected: a candidate somebody else correlated
  // while the panel was open is skipped rather than failing the batch.
  const addCorrelations = useCallback((candidates: CandidateStandard[]) => (
    sendBulkAdd(candidates)
      .then(({ data }: BatchResponse) => {
        const count = data.ids.length;

        if (count === 0) {
          batchFailed();
          return;
        }

        showUndoToast({
          announcement: I18n.t('n_correlations_added_announcement', { count }),
          message: I18n.t('n_correlations_added', { count }),
          onUndo: () => undoBatch(data.ids),
          toastId: 'candidate-bulk',
        });
      })
      .catch(batchFailed)
  ), [batchFailed, sendBulkAdd, undoBatch]);

  return { addCorrelation, addCorrelations };
}
